import React, { useRef, useState, useEffect } from 'react';
import { PlayIcon, PauseIcon, SpeakerHighIcon, SpeakerSlashIcon, ArrowsClockwiseIcon } from '@phosphor-icons/react';

interface AudioPlayerProps {
  src?: string;
  blob?: Blob;
  className?: string;
  onEnded?: () => void;
  autoPlay?: boolean;
  showWaveform?: boolean;
  compact?: boolean;
  knownDuration?: number; // For blobs with unreliable duration metadata
}

export const AudioPlayer: React.FC<AudioPlayerProps> = ({
  src,
  blob,
  className = '',
  onEnded,
  autoPlay = false,
  showWaveform = false,
  compact = false,
  knownDuration,
}) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const waveformRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(knownDuration || 0);
  const [isMuted, setIsMuted] = useState(false);
  const [isLooping, setIsLooping] = useState(false);
  const [audioSrc, setAudioSrc] = useState<string>('');
  const [waveformData, setWaveformData] = useState<number[]>([]);

  // Handle blob to URL conversion
  useEffect(() => {
    if (blob) {
      const url = URL.createObjectURL(blob);
      setAudioSrc(url);
      return () => URL.revokeObjectURL(url);
    } else if (src) {
      setAudioSrc(src);
    }
  }, [blob, src]);

  // Handle autoplay
  useEffect(() => {
    if (autoPlay && audioRef.current && audioSrc) {
      audioRef.current.play().catch(console.warn);
    }
  }, [autoPlay, audioSrc]);

  // Sync knownDuration prop
  useEffect(() => {
    if (knownDuration && knownDuration > 0) {
      setDuration(knownDuration);
    }
  }, [knownDuration]);

  // Generate waveform from audio data
  useEffect(() => {
    if (!showWaveform || !blob) {
      setWaveformData([]);
      return;
    }

    const generateWaveform = async () => {
      try {
        const audioContext = new AudioContext();
        const arrayBuffer = await blob.arrayBuffer();
        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

        // Get audio data from the first channel
        const channelData = audioBuffer.getChannelData(0);
        const samples = 50; // Number of bars in waveform
        const blockSize = Math.floor(channelData.length / samples);
        const waveform: number[] = [];

        for (let i = 0; i < samples; i++) {
          const start = i * blockSize;
          let sum = 0;

          // Get average amplitude for this block
          for (let j = 0; j < blockSize; j++) {
            sum += Math.abs(channelData[start + j] || 0);
          }

          const avg = sum / blockSize;
          // Normalize to 0-1 range with some boost for visibility
          waveform.push(Math.min(1, avg * 3));
        }

        setWaveformData(waveform);
        audioContext.close();
      } catch (error) {
        console.warn('Failed to generate waveform:', error);
        // Fallback to empty waveform
        setWaveformData([]);
      }
    };

    generateWaveform();
  }, [blob, showWaveform]);

  // Smooth animation loop for waveform progress
  useEffect(() => {
    const updateProgress = () => {
      if (audioRef.current && isPlaying) {
        setCurrentTime(audioRef.current.currentTime);
        animationRef.current = requestAnimationFrame(updateProgress);
      }
    };

    if (isPlaying) {
      animationRef.current = requestAnimationFrame(updateProgress);
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isPlaying]);

  const togglePlay = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play().catch(console.warn);
    }
  };

  const toggleMute = () => {
    if (!audioRef.current) return;
    audioRef.current.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  const toggleLoop = () => {
    setIsLooping(!isLooping);
  };

  const handleTimeUpdate = () => {
    if (!audioRef.current) return;
    setCurrentTime(audioRef.current.currentTime);
  };

  const handleLoadedMetadata = () => {
    if (!audioRef.current) return;
    const audioDuration = audioRef.current.duration;
    // Only use browser duration if valid (WebM blobs often return Infinity)
    if (isFinite(audioDuration) && audioDuration > 0) {
      setDuration(audioDuration);
    }
  };

  const handleDurationChange = () => {
    if (!audioRef.current) return;
    const audioDuration = audioRef.current.duration;
    // Update duration when it becomes available
    if (isFinite(audioDuration) && audioDuration > 0) {
      setDuration(audioDuration);
    }
  };

  const handleEnded = () => {
    setCurrentTime(0);

    if (isLooping && audioRef.current) {
      // Replay after 1 second delay when looping
      setTimeout(() => {
        if (audioRef.current) {
          audioRef.current.currentTime = 0;
          audioRef.current.play().catch(console.warn);
        }
      }, 1000);
    } else {
      setIsPlaying(false);
      onEnded?.();
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!audioRef.current) return;
    const time = parseFloat(e.target.value);
    audioRef.current.currentTime = time;
    setCurrentTime(time);
  };

  // Handle waveform click for seeking
  const handleWaveformClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!audioRef.current || !waveformRef.current || !duration) return;

    const rect = waveformRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = clickX / rect.width;
    const newTime = percentage * duration;

    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const formatTime = (seconds: number): string => {
    // Handle Infinity, NaN, or invalid values
    if (!isFinite(seconds) || isNaN(seconds) || seconds < 0) {
      return '0:00';
    }
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
  const waveformSamples = waveformData.length > 0 ? waveformData : Array.from({ length: 50 }, () => 0.15);

  if (!audioSrc) {
    return (
      <div className={`flex items-center justify-center p-4 bg-carbon rounded-xl border border-graphite ${className}`}>
        <span className="text-sm text-iron">No audio available</span>
      </div>
    );
  }

  if (compact) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <audio
          ref={audioRef}
          src={audioSrc}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onDurationChange={handleDurationChange}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onEnded={handleEnded}
        />
        <button
          onClick={togglePlay}
          className="w-8 h-8 rounded-full bg-slate-blue hover:bg-[#8583f9] flex items-center justify-center transition-colors"
        >
          {isPlaying ? (
            <PauseIcon className="w-4 h-4 text-white" weight="fill" />
          ) : (
            <PlayIcon className="w-4 h-4 text-white" weight="fill" />
          )}
        </button>
        <span className="text-xs text-dust/60 min-w-[60px]">
          {formatTime(currentTime)} / {formatTime(duration)}
        </span>
      </div>
    );
  }

  // Unified waveform player with integrated controls
  if (showWaveform) {
    return (
      <div className={`bg-onyx rounded-xl p-4 ${className}`}>
        <audio
          ref={audioRef}
          src={audioSrc}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onDurationChange={handleDurationChange}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onEnded={handleEnded}
        />

        <div className="flex items-center gap-3">
          {/* Play/Pause Button */}
          <button
            onClick={togglePlay}
            className="w-10 h-10 rounded-full bg-slate-blue hover:bg-[#8583f9] flex items-center justify-center transition-colors shrink-0"
          >
            {isPlaying ? (
              <PauseIcon className="w-5 h-5 text-white" weight="fill" />
            ) : (
              <PlayIcon className="w-5 h-5 text-white" weight="fill" />
            )}
          </button>

          {/* Waveform */}
          <div
            ref={waveformRef}
            onClick={handleWaveformClick}
            className="flex-1 h-14 flex items-center gap-[3px] cursor-pointer group overflow-hidden"
          >
            {waveformSamples.map((amplitude, i) => {
              const height = Math.max(0.15, amplitude);
              const barProgress = (i + 1) / waveformSamples.length;
              const isActive = barProgress <= progress / 100;
              return (
                <div
                  key={i}
                  className={`flex-1 rounded-full ${
                    isActive ? 'bg-slate-blue' : 'bg-graphite group-hover:bg-iron'
                  }`}
                  style={{ height: `${height * 100}%` }}
                />
              );
            })}
          </div>

          {/* Time */}
          <span className="text-xs text-dust/50 tabular-nums shrink-0">
            {formatTime(currentTime)} / {formatTime(duration)}
          </span>

          {/* Loop Button */}
          <button
            onClick={toggleLoop}
            className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors shrink-0 ${
              isLooping ? 'bg-slate-blue/20 text-slate-blue' : 'hover:bg-graphite text-dust/50'
            }`}
            title={isLooping ? 'Disable loop' : 'Enable loop'}
          >
            <ArrowsClockwiseIcon className="w-4 h-4" />
          </button>

          {/* Mute Button */}
          <button
            onClick={toggleMute}
            className="w-7 h-7 rounded-lg hover:bg-graphite flex items-center justify-center transition-colors shrink-0"
          >
            {isMuted ? (
              <SpeakerSlashIcon className="w-4 h-4 text-iron" />
            ) : (
              <SpeakerHighIcon className="w-4 h-4 text-dust/50" />
            )}
          </button>
        </div>
      </div>
    );
  }

  // Standard progress bar player (no waveform)
  return (
    <div className={`bg-carbon rounded-xl border border-graphite p-3 ${className}`}>
      <audio
        ref={audioRef}
        src={audioSrc}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onDurationChange={handleDurationChange}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={handleEnded}
      />

      <div className="flex items-center gap-3">
        {/* Play/Pause Button */}
        <button
          onClick={togglePlay}
          className="w-9 h-9 rounded-full bg-slate-blue hover:bg-[#8583f9] flex items-center justify-center transition-colors shrink-0"
        >
          {isPlaying ? (
            <PauseIcon className="w-4 h-4 text-white" weight="fill" />
          ) : (
            <PlayIcon className="w-4 h-4 text-white" weight="fill" />
          )}
        </button>

        {/* Time */}
        <span className="text-xs text-dust/60 min-w-[32px] tabular-nums">
          {formatTime(currentTime)}
        </span>

        {/* Progress Bar */}
        <div className="flex-1 relative h-1.5 bg-graphite rounded-full overflow-hidden">
          <div
            className="absolute left-0 top-0 h-full bg-slate-blue rounded-full transition-[width] duration-200 ease-linear"
            style={{ width: `${progress}%` }}
          />
          <input
            type="range"
            min={0}
            max={duration || 100}
            step={0.1}
            value={currentTime}
            onChange={handleSeek}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          />
        </div>

        {/* Duration */}
        <span className="text-xs text-dust/60 min-w-[32px] tabular-nums">
          {formatTime(duration)}
        </span>

        {/* Loop Button */}
        <button
          onClick={toggleLoop}
          className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors shrink-0 ${
            isLooping ? 'bg-slate-blue/20 text-slate-blue' : 'hover:bg-graphite text-dust/60'
          }`}
          title={isLooping ? 'Disable loop' : 'Enable loop'}
        >
          <ArrowsClockwiseIcon className="w-4 h-4" />
        </button>

        {/* Mute Button */}
        <button
          onClick={toggleMute}
          className="w-7 h-7 rounded-lg hover:bg-graphite flex items-center justify-center transition-colors shrink-0"
        >
          {isMuted ? (
            <SpeakerSlashIcon className="w-4 h-4 text-iron" />
          ) : (
            <SpeakerHighIcon className="w-4 h-4 text-dust/60" />
          )}
        </button>
      </div>
    </div>
  );
};

export default AudioPlayer;
